export type Direction = 'up' | 'down' | 'left' | 'right';

interface FindNearestElementOptions {
  currentElement: HTMLElement;
  direction: Direction;
  elements: HTMLElement[];
}

interface Axis {
  center: number;
  end: number;
  size: number;
  start: number;
}

const getAxis = (rect: DOMRect, vertical: boolean): Axis => {
  const start = vertical ? rect.top : rect.left;
  const size = vertical ? rect.height : rect.width;

  return { center: start + size / 2, end: start + size, size, start };
};

const getForwardGap = (
  current: Axis,
  candidate: Axis,
  positiveDirection: boolean,
): number =>
  positiveDirection
    ? Math.max(0, candidate.start - current.end)
    : Math.max(0, current.start - candidate.end);

const getGap = (first: Axis, second: Axis): number =>
  Math.max(
    0,
    Math.max(first.start, second.start) - Math.min(first.end, second.end),
  );

const getOverlap = (first: Axis, second: Axis): number =>
  Math.max(
    0,
    Math.min(first.end, second.end) - Math.max(first.start, second.start),
  );

const getCandidateScore = (
  currentRect: DOMRect,
  candidateRect: DOMRect,
  direction: Direction,
): number | null => {
  const vertical = direction === 'up' || direction === 'down';
  const positiveDirection = direction === 'down' || direction === 'right';
  const currentPrimary = getAxis(currentRect, vertical);
  const candidatePrimary = getAxis(candidateRect, vertical);
  const currentSecondary = getAxis(currentRect, !vertical);
  const candidateSecondary = getAxis(candidateRect, !vertical);
  const signedPrimaryDistance = positiveDirection
    ? candidatePrimary.center - currentPrimary.center
    : currentPrimary.center - candidatePrimary.center;
  const directionalTolerance = Math.min(
    20,
    Math.min(currentPrimary.size, candidatePrimary.size) * 0.2,
  );

  // Focus styles may move the active item by a few pixels. Treat nearby
  // centers as the same row or column so that transform does not change the
  // meaning of an arrow key.
  if (signedPrimaryDistance <= directionalTolerance) return null;

  const primaryGap = getForwardGap(
    currentPrimary,
    candidatePrimary,
    positiveDirection,
  );
  const secondaryDistance = Math.abs(
    candidateSecondary.center - currentSecondary.center,
  );
  const secondaryGap = getGap(currentSecondary, candidateSecondary);
  const secondaryOverlap = getOverlap(currentSecondary, candidateSecondary);
  const overlapRatio =
    secondaryOverlap /
    Math.max(1, Math.min(currentSecondary.size, candidateSecondary.size));

  // Strongly prefer candidates that share the current row or column. A
  // diagonal candidate can still win when the aligned candidate is far away.
  const alignmentPenalty = secondaryOverlap
    ? secondaryDistance * (0.15 + (1 - overlapRatio) * 0.35)
    : 240 + secondaryGap * 3 + secondaryDistance * 0.5;

  return primaryGap + signedPrimaryDistance * 0.15 + alignmentPenalty;
};

export const findNearestElement = ({
  currentElement,
  direction,
  elements,
}: FindNearestElementOptions): HTMLElement | null => {
  const currentRect = currentElement.getBoundingClientRect();
  let bestElement: HTMLElement | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  elements.forEach((element) => {
    if (element === currentElement || element.hasAttribute('disabled')) return;

    const score = getCandidateScore(
      currentRect,
      element.getBoundingClientRect(),
      direction,
    );

    if (score !== null && score < bestScore) {
      bestElement = element;
      bestScore = score;
    }
  });

  return bestElement;
};
